import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { toast } from "react-toastify";
import { fetchPremiumCdBalanceList } from "@actions/fetchCDBalanceAction";
import { usePagination } from "@components";
import { useAppConfig } from "@context/AppConfigContext";
import {
  submitAmountData,
  verifyOtpAddCdBalance,
} from "@actions/amountSubmissionDataAction";
import { validateAmount } from "@utils";
import { useCSVExport } from "@hooks/useCSVExport";
import {
  formatDate,
  formatDateToYYYYMMDD,
  isValidDateRange,
} from "@utils/dateUtils";
import { MIS_FIELDS_CDBALANCE } from "@constants/misDownloadData";

// Constants
export const PREMIUM_CD_BALANCE_STATUS = [
  { value: "Credit", label: "Credit" },
  { value: "Debit", label: "Debit" },
];

const ERROR_MESSAGES = {
  selectPartner: "Please select a partner",
  enterValidAmount: "Please enter a valid amount",
  numbersOnly: "Only numbers are allowed",
  maxLength: "Amount cannot exceed 10 digits",
  fetchFailed: "Failed to fetch CD Balance Premium data",
  submitFailed: "Failed to submit data",
};

const DEFAULT_CLIENT_NAME = "emt";
const MAX_AMOUNT_LENGTH = 10;
const ITEMS_PER_PAGE = 10;

// Data mapper for MIS export
const mapCdBalancePremiumToMISData = (balance, index) => ({
  "SR. No ": index + 1,
  "Updated By": balance.user_name || "-",
  "Txn Type": balance.txn_type || "-",
  Mode: balance.mode || "-",
  Date: formatDate(balance.txn_date) || "-",
  Status: balance.txn_status || "-",
  "Updated Amount": balance.amount || "-",
  "Txn Amount": balance.amount_difference || "-",
  "PNR Number": balance.pnr_number || "-",
});

// Helper function to build API parameters
const buildAPIParams = (client, statusFilter, fromDate, toDate) => {
  const params = {
    clientName: client?.clientName || DEFAULT_CLIENT_NAME,
  };

  if (statusFilter) {
    params.txn_type = statusFilter;
  }

  if (isValidDateRange(fromDate, toDate, false)) {
    params.startDate = formatDateToYYYYMMDD(fromDate);
    params.endDate = formatDateToYYYYMMDD(toDate);
  }

  return params;
};

/**
 * Custom hook for CD Balance Premium management
 */
export const useCDBalanceData = () => {
  // State
  const [balanceAmount, setBalanceAmount] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [premiumCdBalances, setPremiumCdBalances] = useState([]);
  const [currentBalance, setCurrentBalance] = useState(0);
  const [fromDate, setFromDate] = useState(new Date());
  const [toDate, setToDate] = useState(new Date());
  const [selectedStatusFilter, setSelectedStatusFilter] = useState("");
  const [otpDetails, setOtpDetails] = useState({
    showOtpDrawer: false,
    otpValue: "",
    otpError: "",
    otpSubmitLoading: false,
  });
  const [otpReqId, setOtpReqId] = useState("");

  // Hooks
  const { user, selectedClient } = useAppConfig();
  const isInitialMount = useRef(true);
  const { exportToXLSX, generateDateFilename } = useCSVExport();

  // Memoized values
  const userInfo = useMemo(
    () => ({
      name: user?.user?.name,
      email: user?.user?.email,
      id: String(user?.user?.id || ""),
    }),
    [user],
  );

  const currentParams = useMemo(
    () =>
      buildAPIParams(selectedClient, selectedStatusFilter, fromDate, toDate),
    [selectedClient, selectedStatusFilter, fromDate, toDate],
  );

  // Handle amount input change with validation
  const handleAmountChange = useCallback((e) => {
    const value = e.target.value;

    if (value.length > MAX_AMOUNT_LENGTH) {
      setError(ERROR_MESSAGES.maxLength);
      return;
    }

    if (validateAmount(value)) {
      setBalanceAmount(value);
      setError("");
    } else {
      setError(ERROR_MESSAGES.numbersOnly);
    }
  }, []);

  // Load CD Balance data
  const loadPremiumCdBalanceData = useCallback(async (params) => {
    setLoading(true);

    try {
      const response = await fetchPremiumCdBalanceList(params);

      if (response?.status) {
        setPremiumCdBalances(response.balanceHistory || response.data || []);
        setCurrentBalance(response.currentCdBalance || 0);
        setError("");
      } else {
        setPremiumCdBalances([]);
        setCurrentBalance(0);
        setError(response?.message || ERROR_MESSAGES.fetchFailed);
      }
    } catch (err) {
      setPremiumCdBalances([]);
      setCurrentBalance(0);
      setError(ERROR_MESSAGES.fetchFailed);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial data load and filter changes
  useEffect(() => {
    if (isInitialMount.current) {
      const initialParams = buildAPIParams(
        selectedClient,
        "",
        new Date(),
        new Date(),
      );
      isInitialMount.current = false;
      loadPremiumCdBalanceData(initialParams);
    } else if (
      selectedStatusFilter ||
      isValidDateRange(fromDate, toDate, false)
    ) {
      loadPremiumCdBalanceData(currentParams);
    }
  }, [
    selectedStatusFilter,
    fromDate,
    toDate,
    selectedClient,
    loadPremiumCdBalanceData,
    currentParams,
  ]);

  // Handle form submission
  const handleSubmit = useCallback(async () => {
    if (!selectedClient) {
      setError(ERROR_MESSAGES.selectPartner);
      return false;
    }

    if (!balanceAmount || balanceAmount === "0") {
      setError(ERROR_MESSAGES.enterValidAmount);
      return false;
    }

    const requestData = {
      clientName: selectedClient?.clientName,
      amount: balanceAmount,
      user_Name: userInfo.name,
      user_Email: userInfo.email,
      user_Id: userInfo.id,
      Type: "Premium",
    };

    try {
      const submissionData = await submitAmountData(requestData);

      if (submissionData) {
        setOtpReqId(submissionData?.requestId);
        setError("");
        toast.success("OTP sent to your email");
        setOtpDetails((prev) => ({ ...prev, showOtpDrawer: true }));
        return true;
      }
    } catch (err) {
      setError(ERROR_MESSAGES.submitFailed);
    }

    return false;
  }, [selectedClient, balanceAmount, userInfo]);

  // Handle status filter change
  const handleStatusFilterChange = useCallback((status) => {
    setSelectedStatusFilter(status);
  }, []);

  // Handle date range change
  const handleDateChange = useCallback((from, to) => {
    setFromDate(from);
    setToDate(to);
  }, []);

  // Handle MIS data download
  const handleDownloadMIS = useCallback(() => {
    const filename = generateDateFilename("CD_Balance_Premium_Data");
    exportToXLSX(
      premiumCdBalances,
      MIS_FIELDS_CDBALANCE,
      filename,
      mapCdBalancePremiumToMISData,
    );
  }, [premiumCdBalances, exportToXLSX, generateDateFilename]);

  // Clear all filters
  const handleClearFilters = useCallback(() => {
    setFromDate(new Date());
    setToDate(new Date());
    setSelectedStatusFilter("");
  }, []);

  // Pagination functionality
  const {
    currentPage,
    currentData: paginatedCDBalances,
    handlePageChange,
  } = usePagination(premiumCdBalances, ITEMS_PER_PAGE);

  // OTP handlers
  const handleOtpChange = useCallback((value) => {
    setOtpDetails((prev) => ({ ...prev, otpValue: value, otpError: "" }));
  }, []);

  const handleCloseOtpModal = useCallback(() => {
    setBalanceAmount("");
    setOtpDetails({
      showOtpDrawer: false,
      otpValue: "",
      otpError: "",
      otpSubmitLoading: false,
    });
  }, []);

  const handleOtpSubmit = useCallback(async () => {
    const reqParam = {
      otp: otpDetails.otpValue,
      requestId: otpReqId,
    };

    try {
      const response = await verifyOtpAddCdBalance(reqParam);

      if (response?.status) {
        handleCloseOtpModal();
        setBalanceAmount("");
        toast.success(response?.message || "Balance updated Successfully");

        // Reload data
        const reloadParams = buildAPIParams(
          selectedClient,
          "",
          new Date(),
          new Date(),
        );
        loadPremiumCdBalanceData(reloadParams);
      } else {
        setOtpDetails((prev) => ({
          ...prev,
          otpError: response?.message || "Invalid OTP",
        }));
      }
    } catch (err) {
      setOtpDetails((prev) => ({
        ...prev,
        otpError: "Failed to verify OTP",
      }));
    }
  }, [
    otpDetails.otpValue,
    otpReqId,
    handleCloseOtpModal,
    selectedClient,
    loadPremiumCdBalanceData,
  ]);

  const handleResendOTP = useCallback(() => {
    handleSubmit();
  }, [handleSubmit]);

  return {
    // State
    balanceAmount,
    error,
    loading,
    currentBalance,
    fromDate,
    toDate,
    selectedStatusFilter,
    otpDetails,

    // Data
    premiumCdBalances: paginatedCDBalances,
    allCDBalances: premiumCdBalances,
    currentPage,

    // Handlers
    handleAmountChange,
    handleClearFilters,
    handleSubmit,
    handleDownloadMIS,
    handleDateChange,
    handlePageChange,
    handleStatusFilterChange,
    handleOtpChange,
    handleCloseOtpModal,
    handleOtpSubmit,
    handleResendOTP,
  };
};
